import { useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Link2 } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Container } from '@/components/layout/Container'
import { Section } from '@/components/layout/Section'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState, Skeleton } from '@/components/ui/Feedback'
import { useToast } from '@/components/ui/Toast'
import { Gallery } from '@/components/watch/Gallery'
import { SpecTable } from '@/components/watch/SpecTable'
import { EnquiryForm } from '@/components/watch/EnquiryForm'
import { WatchGrid } from '@/components/catalog/WatchGrid'
import { useViewCounter, useWatch } from '@/hooks/useWatches'
import { relatedWatches } from '@/lib/catalog'

/**
 * One piece, in full: photographs on the left, the argument for buying it on
 * the right, specification underneath, then the enquiry form. There is no cart
 * anywhere on this site — the enquiry IS the checkout.
 */
export default function WatchDetail() {
  const { id } = useParams<{ id: string }>()
  const { t, tf, formatPrice } = useI18n()
  const { toast } = useToast()
  const { watch, collection, watches, loading } = useWatch(id)
  const enquiryRef = useRef<HTMLDivElement>(null)

  useViewCounter(watch)

  const related = useMemo(() => (watch ? relatedWatches(watches, watch, 3) : []), [watches, watch])

  if (loading) {
    return (
      <Container size="xl" className="py-16 grid lg:grid-cols-2 gap-12">
        <Skeleton className="aspect-square w-full" />
        <div className="flex flex-col gap-5">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-14 w-4/5" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Container>
    )
  }

  if (!watch) {
    return (
      <Container size="md" className="py-10">
        <EmptyState
          title={t('watch.notFoundTitle')}
          description={t('watch.notFoundBody')}
          action={
            <Link to="/collection">
              <Button>{t('watch.backToCollection')}</Button>
            </Link>
          }
        />
      </Container>
    )
  }

  const sold = watch.status === 'sold'
  const reserved = watch.status === 'reserved'

  const availability = sold
    ? t('status.sold')
    : reserved
      ? t('watch.outOfStock')
      : watch.stock <= 1
        ? t('watch.lastOne')
        : t('watch.inStock')

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast(t('watch.linkCopied'))
    } catch {
      /* clipboard blocked — silence is better than an error toast here */
    }
  }

  return (
    <div data-upbo-id="page-watch-detail" data-upbo-label="Watch detail page">
      <Container size="xl" className="pt-10 pb-16 sm:pb-24">
        {/* ── Back ─────────────────────────────────────────────────────── */}
        <Link
          to="/collection"
          className="inline-flex items-center gap-2 mb-8 text-[11px] uppercase tracking-[0.16em] text-text-muted hover:text-text transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          {t('watch.backToCollection')}
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* ── Photographs ────────────────────────────────────────────── */}
          <div className="lg:sticky lg:top-24">
            <Gallery images={[watch.image, ...(watch.gallery ?? [])]} alt={tf(watch, 'title')} />
          </div>

          {/* ── The argument ───────────────────────────────────────────── */}
          <div className="flex flex-col gap-7">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-[10px] uppercase tracking-[0.18em] text-text-faint">{watch.maison}</span>
                {collection && (
                  <Link to={`/collection?collection=${collection.slug}`}>
                    <Badge tone="primary" size="sm">
                      {tf(collection, 'name')}
                    </Badge>
                  </Link>
                )}
                {(sold || reserved) && (
                  <Badge tone={sold ? 'neutral' : 'warning'} size="sm">
                    {t(`status.${watch.status}`)}
                  </Badge>
                )}
              </div>

              <h1 className="font-display text-[42px] sm:text-[54px] leading-[1.02]">{tf(watch, 'title')}</h1>

              <div className="flex items-baseline gap-4 mt-1">
                <span className="text-2xl text-text">{formatPrice(watch.price)}</span>
                {watch.old_price > watch.price && (
                  <span className="text-base text-text-faint line-through">{formatPrice(watch.old_price)}</span>
                )}
              </div>

              <div className="flex items-center gap-2 text-[12px] text-text-muted">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {t('watch.availability')}: {availability}
              </div>
            </div>

            <p className="text-[15px] text-text-muted leading-relaxed">{tf(watch, 'description')}</p>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => enquiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              >
                {t('watch.enquire')}
              </Button>
              <Button
                size="lg"
                variant="ghost"
                icon={<Link2 size={15} strokeWidth={1.5} />}
                onClick={() => void copyLink()}
              >
                {t('watch.share')}
              </Button>
            </div>

            {/* ── Specification ────────────────────────────────────────── */}
            <div className="flex flex-col gap-3 pt-3">
              <span className="eyebrow">{t('watch.specs')}</span>
              <SpecTable watch={watch} />
            </div>
          </div>
        </div>
      </Container>

      {/* ── Enquiry ──────────────────────────────────────────────────────── */}
      <div ref={enquiryRef} className="bg-surface-2 border-y border-border">
        <Container size="md" className="py-16 sm:py-20">
          <div className="flex flex-col gap-2 mb-8">
            <span className="eyebrow">{t('enquiry.title')}</span>
            <h2 className="font-display text-[32px] leading-tight">{tf(watch, 'title')}</h2>
            <p className="text-sm text-text-muted">{t('enquiry.subtitle')}</p>
          </div>
          <EnquiryForm watch={watch} />
        </Container>
      </div>

      {/* ── Related ──────────────────────────────────────────────────────── */}
      {related.length > 0 && (
        <Section title={t('watch.related')} containerSize="xl">
          <WatchGrid watches={related} columns={3} />
        </Section>
      )}
    </div>
  )
}
